import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { REGIONS } from '@packages/constants';
import { openApiInstances } from '@packages/open-api';
import {
  isAddTripEnabledSelector,
  useConfigStore,
  useDestinationsStore,
  useRegionStore,
} from '@packages/stores';
import { RegionCode } from '@packages/types';

import { Footer, Header, StopSell } from '@components';
import { GetQuoteLoading } from '@components/getQuoteLoading/getQuoteLoading.component';
import { useEmailQuoteReminder, useScrollToTop, useTimeout } from '@hooks';
import { ROUTE_PATHS, navigateToFirstRegionView, router } from '@routes/router';
import { ROUTES } from '@routes/types';
import { captureException, setTag, withScope } from '@sentry/react';
import { useFeatureFlagsStore, useGuestStore, useModalStore, useTripStore } from '@store';
import {
  clearSessionStorage,
  getAnonymousId,
  sendAnalyticsECommerce,
  sendFeatureFlagEvents,
  sentryTags,
  setMarketingIds,
} from '@utils';

import { WebConfigSchema } from '../../../scripts/remoteConfigSchema';
import { WEB_OPEN_APIS } from '../../utils/awsConfig';
import { Emc } from '../checkout';

export const Quote = () => {
  useScrollToTop();
  const isCreatingOrUpdatingTrip = useTripStore(state => state.isCreatingOrUpdatingTrip);
  const isAddTripEnable = useConfigStore(isAddTripEnabledSelector);
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isLoadingQuote = useMemo(
    () => location.pathname.endsWith(`/${ROUTES.LOAD_QUOTE}`) && !!searchParams.get('quoteId'),
    [location.pathname, searchParams],
  );

  const onClose = () => {
    resetModalState();
    resetGuestState();
    window.location.href = `/quote/${params.region}`;
  };
  useTimeout({ onClose });
  useEmailQuoteReminder();

  useLayoutEffect(() => {
    setMarketingIds();
    window.onpopstate = e => {
      if (isCreatingOrUpdatingTrip) {
        e.preventDefault();
        router.navigate(1);
        setIsCreatingOrUpdatingTrip(false);
      }
    };
  }, [isCreatingOrUpdatingTrip]);

  useEffect(() => {
    setTag('section', 'createQuote');
  }, []);

  useEffect(() => {
    withScope(() => {
      sentryTags({ event: 'effect.init', source: 'quote.view.tsx' });
      (() => {
        const paramRegion = params.region?.toUpperCase();
        openApiInstances.Quote.defaults.headers.common['x-platform'] = 'web';
        openApiInstances.Quote.defaults.headers.common.platform = 'web';
        const currentRegion = getCurrentRegionFromUrlParams(paramRegion) ?? 'AU';
        setRegion({ country: currentRegion });
        const QuoteApiConfig = WEB_OPEN_APIS.endpoints.find(endpoint => endpoint.name === 'Quote');

        if (QuoteApiConfig) {
          const regionParam = `/${paramRegion?.toLowerCase() ?? 'au'}`;
          openApiInstances.Quote.defaults.baseURL = QuoteApiConfig.endpoint + regionParam;
          openApiInstances.Quote.defaults.headers.common['x-api-key'] = regionParam?.includes('au')
            ? import.meta.env.VITE_AU_OPEN_API_KEY
            : import.meta.env.VITE_US_OPEN_API_KEY;
        }

        // Run independent startup tasks concurrently
        // retrieves config and destinations from BE
        Promise.all([
          getFeatureFlags(currentRegion),
          getRemoteConfig(currentRegion),
          getTripDestinations(),
        ]).catch(captureException);

        if (isLoadingQuote) {
          return;
        }
        // disables users from jumping ahead of create quote flow without selecting destination
        if (
          getBaseRoutes(currentRegion).includes(window.location.pathname) ||
          !useTripStore.getState()?.trip?.sortKey
        ) {
          resetTripState();
          navigateToFirstRegionView();
          clearSessionStorage();
        }
      })();
    });
  }, [isLoadingQuote, params.region]);

  useEffect(() => {
    sendAnalyticsECommerce('Cart Viewed');
  }, []);

  if (!isAddTripEnable) {
    return <StopSell />;
  }

  return (
    <div
      className={twMerge(
        'flex min-h-screen flex-col',
        ROUTE_PATHS().confirmation === location.pathname && 'bg-surface-page',
      )}>
      <Header />
      <Outlet />
      <GetQuoteLoading />
      <Emc />
      <Footer />
    </div>
  );
};

const setRegion = useRegionStore.getState().setRegion;
const initFeatureFlags = useFeatureFlagsStore.getState().initFeatureFlags;
const setIsCreatingOrUpdatingTrip = useTripStore.getState().setIsCreatingOrUpdatingTrip;
const resetModalState = useModalStore.getState().resetModalState;
const resetTripState = useTripStore.getState().resetTripState;
const resetGuestState = useGuestStore.getState().resetGuestState;
const getTripDestinations = useDestinationsStore.getState().getDestinations;
const getConfig = useConfigStore.getState().getConfig;
const setRegionSpecificConfig = useConfigStore.getState().setRegionSpecificConfig;

const getBaseRoutes = (currentRegion: RegionCode) => {
  const routePaths = ROUTE_PATHS();
  return [
    `/${ROUTES.QUOTE}/${currentRegion.toLowerCase()}`,
    routePaths.tripDestinations,
    routePaths.tripDates,
    routePaths.tripTravellers,
    routePaths.tripCost,
    routePaths.stateOfResidence,
  ];
};

const getCurrentRegionFromUrlParams = (paramRegion?: string) => {
  const availableRegions = Object.keys(REGIONS) as RegionCode[];
  return availableRegions.find(region => region === paramRegion);
};

const getRemoteConfig = async (currentRegion: RegionCode) => {
  try {
    const config = await getConfig();
    setRegionSpecificConfig({ country: currentRegion });
    const webConfigResult = WebConfigSchema.safeParse(config);
    if (!webConfigResult.success) {
      captureException(new Error('AppConfig failed validation'), {
        extra: { validationError: webConfigResult.error.format() },
      });
    }
    return webConfigResult.data;
  } catch (error) {
    captureException(new Error('AppConfig failed to fetch'), { extra: { error } });
  }
};

const getFeatureFlags = async (currentRegion: RegionCode) => {
  try {
    const anonymousId = await getAnonymousId();
    if (anonymousId) {
      const featureFlags = await initFeatureFlags({
        region: currentRegion,
        platform: 'web',
        anonymousId,
        apiKey: import.meta.env.VITE_POSTHOG_API_KEY,
        apiURL: import.meta.env.VITE_POSTHOG_API_HOST,
      });
      sendFeatureFlagEvents(featureFlags);
    }
  } catch (e) {
    captureException(e);
  }
};
